package domain.player

import engine.model.HandlingConfig

/**
 * Application-wide singleton that owns the active [PlayerProfileRepository].
 *
 * All other code reads from and writes through [PlayerSession] — never
 * directly to storage. To switch to a different backend, replace [repository]
 * before any scene is loaded:
 *
 * ```kotlin
 * PlayerSession.repository = FilePlayerProfileRepository()
 * ```
 *
 * The session loads the profile eagerly on first access. Callers that need
 * the profile for the first time should call [init] during app startup.
 */
object PlayerSession {

    /** Swap this out to change the backing store. Must be set before [init] is called. */
    var repository: PlayerProfileRepository = NoOpPlayerProfileRepository()

    /** The currently loaded profile. Updated on every write. */
    var profile: PlayerProfile = PlayerProfile()
        private set

    // ── Init ──────────────────────────────────────────────────────────────────

    /**
     * Load the profile from the repository. Call once at app startup.
     * [onReady] fires when the profile is available (immediately for local,
     * asynchronously for remote).
     */
    fun init(onReady: ((PlayerProfile) -> Unit)? = null) {
        repository.loadProfile { loaded ->
            profile = loaded
            onReady?.invoke(loaded)
        }
    }

    // ── Auth ──────────────────────────────────────────────────────────────────

    val isLoggedIn: Boolean get() = repository.isLoggedIn
    val username: String get() = repository.username

    fun login(username: String, onResult: (PlayerProfile) -> Unit) {
        repository.login(username) { loaded ->
            profile = loaded
            onResult(loaded)
        }
    }

    fun logout() {
        repository.logout()
        profile = PlayerProfile()
    }

    // ── Reads ─────────────────────────────────────────────────────────────────

    val handling: HandlingConfig get() = profile.handling
    val graphics: GraphicsConfig get() = profile.graphics
    val keyBindings: Map<GameAction, List<String>> get() = profile.keyBindings

    // ── Writes ────────────────────────────────────────────────────────────────

    fun updateHandling(handling: HandlingConfig) {
        repository.updateHandling(handling)
        profile = profile.copy(handling = handling)
    }

    fun updateGraphics(graphics: GraphicsConfig) {
        repository.updateGraphics(graphics)
        profile = profile.copy(graphics = graphics)
    }

    fun updateKeyBindings(bindings: Map<GameAction, List<String>>) {
        repository.updateKeyBindings(bindings)
        profile = profile.copy(keyBindings = bindings)
    }

    fun resetToDefaults() {
        profile = repository.resetToDefaults()
    }
}

/**
 * No-op repository used as a placeholder until a real implementation is injected.
 * Returns default profiles and ignores all writes.
 */
private class NoOpPlayerProfileRepository : PlayerProfileRepository {
    override val isLoggedIn: Boolean = false
    override val username: String = "guest"
    override fun loadProfile(onResult: (PlayerProfile) -> Unit) = onResult(PlayerProfile())
    override fun saveProfile(profile: PlayerProfile) = Unit
    override fun updateHandling(handling: HandlingConfig) = Unit
    override fun updateGraphics(graphics: GraphicsConfig) = Unit
    override fun updateKeyBindings(bindings: Map<GameAction, List<String>>) = Unit
    override fun login(username: String, onResult: (PlayerProfile) -> Unit) = onResult(PlayerProfile(username = username))
    override fun logout() = Unit
    override fun resetToDefaults(): PlayerProfile = PlayerProfile()
}
